import React, { useState } from 'react';
import api from '../../utils/api';

/**
 * Mobile-optimized InviteButton that uses native share sheet
 * Falls back to copy-to-clipboard if Web Share API is not available
 */
const MobileInviteButton = ({ isAuthenticated = false, onSignIn, roomId }) => {
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleInviteClick = async () => {
    // If not authenticated, prompt to sign in
    if (!isAuthenticated) {
      onSignIn();
      return;
    }

    try {
      setLoading(true);
      setFeedback('');

      // Get the invite URL from the API
      let inviteUrl = '';
      if (roomId) {
        try {
          const response = await api.getRoomInviteLink(roomId);
          inviteUrl = response.inviteUrl || window.location.origin;
        } catch (error) {
          console.error('Error getting invite URL:', error);
          inviteUrl = window.location.origin;
        }
      } else {
        inviteUrl = window.location.origin;
      }

      // Check if Web Share API is available (mobile browsers)
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join me in PlayScreen!',
            text: "I'm in this story now, come join me!",
            url: inviteUrl,
          });
          
          // Show brief success feedback
          setFeedback('Shared!');
          setTimeout(() => setFeedback(''), 2000);
        } catch (error) {
          // User cancelled or error occurred
          if (error.name !== 'AbortError') {
            console.error('Error sharing:', error);
            // Fallback to copy
            await fallbackCopy(inviteUrl);
          }
        }
      } else {
        // Fallback to copy for browsers without Web Share API
        await fallbackCopy(inviteUrl);
      }
    } catch (error) {
      console.error('Error in invite:', error);
      setFeedback('Error');
      setTimeout(() => setFeedback(''), 2000);
    } finally {
      setLoading(false);
    }
  };

  const fallbackCopy = async (url) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setFeedback('Copied!');
      setTimeout(() => setFeedback(''), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      setFeedback('Error');
      setTimeout(() => setFeedback(''), 2000);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleInviteClick}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
          </svg>
        )}
        <span>{isAuthenticated ? 'Invite Friends' : 'Sign In'}</span>
      </button>
      
      {/* Feedback Toast */}
      {feedback && (
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-700 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
          {feedback}
        </div>
      )}
    </div>
  );
};

export default MobileInviteButton;